import React, { useState } from 'react';
import { Tag, X, ShoppingBag } from 'lucide-react';
import { useCart } from '@context/CartContext';
import { formatCurrency } from '@utils/helpers';
import { PRICING } from '@utils/constants';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import Alert from '@components/common/Alert';

interface CartSummaryProps {
  className?: string;
  showCheckoutButton?: boolean;
  onCheckout?: () => void;
}

const CartSummary: React.FC<CartSummaryProps> = ({
  className,
  showCheckoutButton = true,
  onCheckout,
}) => {
  const [discountCode, setDiscountCode] = useState('');
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);

  const {
    items,
    itemCount,
    subtotal,
    total,
    discountAmount,
    discountCode: appliedDiscountCode,
    loading,
    applyDiscount,
    removeDiscount,
  } = useCart();

  // Calculate package discount info
  const getPackageDiscountInfo = () => {
    if (itemCount >= PRICING.packages.large.threshold) {
      return PRICING.packages.large;
    } else if (itemCount >= PRICING.packages.medium.threshold) {
      return PRICING.packages.medium;
    } else if (itemCount >= PRICING.packages.small.threshold) {
      return PRICING.packages.small;
    }
    return null;
  };

  const packageDiscount = getPackageDiscountInfo();
  const packageDiscountAmount = packageDiscount ? subtotal * packageDiscount.discount : 0;
  const codeDiscountAmount = appliedDiscountCode ? discountAmount - packageDiscountAmount : 0;

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return;

    setIsApplyingDiscount(true);
    setDiscountError(null);

    try {
      const success = await applyDiscount(discountCode.trim());
      if (success) {
        setDiscountCode('');
      } else {
        setDiscountError('Invalid discount code');
      }
    } catch (error: any) {
      setDiscountError(error.message || 'Failed to apply discount code');
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleApplyDiscount();
    }
  };

  if (items.length === 0) {
    return (
      <div className={className}>
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <ShoppingBag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Your cart is empty</h3>
          <p className="text-gray-500">Start browsing events to add photos to your cart.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>

        {/* Items count */}
        <div className="flex justify-between items-center py-2 text-sm">
          <span className="text-gray-600">
            {itemCount} item{itemCount !== 1 ? 's' : ''}
          </span>
          <span className="font-medium">{formatCurrency(subtotal)}</span>
        </div>

        {/* Package discount */}
        {packageDiscount && packageDiscountAmount > 0 && (
          <div className="flex justify-between items-center py-2 text-sm text-green-600">
            <span>{packageDiscount.name}</span>
            <span>-{formatCurrency(packageDiscountAmount)}</span>
          </div>
        )}

        {/* Applied discount code */}
        {appliedDiscountCode && codeDiscountAmount > 0 && (
          <div className="flex justify-between items-center py-2 text-sm text-green-600">
            <div className="flex items-center gap-2">
              <span>Discount: {appliedDiscountCode}</span>
              <Button
                size="small"
                variant="ghost"
                className="text-red-500 hover:text-red-600 p-1 h-auto"
                onClick={removeDiscount}
                icon={<X size={12} />}
              />
            </div>
            <span>-{formatCurrency(codeDiscountAmount)}</span>
          </div>
        )}

        {/* Discount code input */}
        {!appliedDiscountCode && (
          <div className="mt-4 mb-4">
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Discount code"
                value={discountCode}
                onChange={e => setDiscountCode(e.target.value)}
                onKeyPress={handleKeyPress}
                leftIcon={<Tag size={16} />}
                className="flex-1"
                disabled={isApplyingDiscount}
              />
              <Button
                variant="outline"
                onClick={handleApplyDiscount}
                loading={isApplyingDiscount}
                disabled={!discountCode.trim() || isApplyingDiscount}
              >
                Apply
              </Button>
            </div>
            {discountError && (
              <Alert
                type="error"
                message={discountError}
                className="mt-2"
                dismissible
                onDismiss={() => setDiscountError(null)}
              />
            )}
          </div>
        )}

        {/* Next discount threshold info */}
        {!packageDiscount && itemCount > 0 && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              Add {PRICING.packages.small.threshold - itemCount} more items to get{' '}
              {Math.round(PRICING.packages.small.discount * 100)}% off!
            </p>
          </div>
        )}

        {packageDiscount && packageDiscount !== PRICING.packages.large && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              {packageDiscount === PRICING.packages.small ? (
                <>
                  Add {PRICING.packages.medium.threshold - itemCount} more items to get{' '}
                  {Math.round(PRICING.packages.medium.discount * 100)}% off!
                </>
              ) : (
                <>
                  Add {PRICING.packages.large.threshold - itemCount} more items to get{' '}
                  {Math.round(PRICING.packages.large.discount * 100)}% off!
                </>
              )}
            </p>
          </div>
        )}

        <div className="border-t pt-4 mt-4">
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold text-gray-900">Total</span>
            <span className="text-lg font-semibold text-gray-900">{formatCurrency(total)}</span>
          </div>

          {discountAmount > 0 && (
            <div className="text-sm text-green-600 text-right mt-1">
              You save {formatCurrency(discountAmount)}
            </div>
          )}
        </div>

        {showCheckoutButton && (
          <Button
            variant="primary"
            size="large"
            className="w-full mt-4"
            onClick={onCheckout}
            disabled={loading || items.length === 0}
            loading={loading}
          >
            Proceed to Checkout
          </Button>
        )}

        {/* Payment security info */}
        <div className="mt-4 text-center text-xs text-gray-500">
          <p>🔒 Secure payment with 256-bit SSL encryption</p>
        </div>
      </div>
    </div>
  );
};

export default CartSummary;
